import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {PersonnelDTO, User} from '@/types/gatewayDataModels';

import {CommonUserDataState} from './stateTypes';

const initialState: CommonUserDataState = {
    phoneCallData: {
        callerPatientId: null,
        callerFirstName: '',
        callerLastName: '',
        callerSex: '',
        callerDOB: '',
        isOutboundCallActive: false,
        showAnswerButton: false,
        awsConnectAgentStatus: 'Offline',
        initialContactId: '',
        sourceAgentEmail: '',
        queueARN: [],
        needToShowCPPPanel: false,
        isDoNotDisturbedEnabled: false,
    },
    tokenData: {},
    missionControlIds: [],
    selectedMissionControl: null,
    permissions: {},
    patientPageTabs: [],
    routes: [],
    //user data from Auth0
    user: {roles: []},
    //user data from database
    employment: {},
    features: {},
    leftNavbarOpen: false,
    loadingHandler: {loadInitialUserDataLoading: true},
    isWSConnected: true,
};

const SLICE_NAME = 'commonUserDataReducer';
const {actions, reducer} = createSlice({
    name: SLICE_NAME,
    initialState,
    reducers: {
        toggleLeftSidebar: (state, action: PayloadAction<boolean>) => {
            state.leftNavbarOpen = action.payload;
        },
        setUserInitialData: (
            state,
            action: PayloadAction<{
                user: User;
                employment: PersonnelDTO;
                tokenData:
                    | {
                          routes: {entity?: string; exact?: boolean; route: string; title: string; access?: [string]}[];
                          missionControlIds: number[];
                          permissions: {[key: string]: boolean};
                      }
                    | {missionControlIds: number[]};
            }>,
        ) => {
            const {user, employment, tokenData} = action.payload;

            Object.assign(state, {
                user,
                employment,
                loadingHandler: {...state.loadingHandler, loadInitialUserDataLoading: false},
                ...tokenData,
            });

            localStorage.setItem('reloadTime', JSON.stringify(Date.now()));
        },
        updateUserData: (state) => state,
        updateUserDataSuccess: (state, action: PayloadAction<PersonnelDTO>) => {
            state.employment = action.payload;
        },
        getUserInitialData: (
            state,
            _action: PayloadAction<{
                user: User;
                tokenData:
                    | {
                          routes: {entity?: string; exact?: boolean; route: string; title: string; access?: string[]}[];
                          missionControlIds: number[];
                          permissions: {[key: string]: boolean};
                      }
                    | {missionControlIds: number[]};
            }>,
        ) => {
            return state;
        },
        setSelectedMissionControl: (state, action: PayloadAction<number>) => {
            state.selectedMissionControl = action.payload;
        },
        setOutboundCallStatus: (
            state,
            action: PayloadAction<{isOutboundCallActive: boolean; showAnswerButton?: boolean}>,
        ) => {
            const {isOutboundCallActive, showAnswerButton} = action.payload;

            state.phoneCallData = {
                ...state.phoneCallData,
                isOutboundCallActive,
                showAnswerButton:
                    showAnswerButton || showAnswerButton === false
                        ? showAnswerButton
                        : state.phoneCallData.showAnswerButton,
            };
        },
        setAwsConnectAgentStatus: (state, action: PayloadAction<string>) => {
            state.phoneCallData = {...state.phoneCallData, awsConnectAgentStatus: action.payload};
        },
        setConnectingPhoneStatuses: (
            state,
            action: PayloadAction<{initialContactId: string; isIncomingCall: boolean}>,
        ) => {
            const {initialContactId, isIncomingCall} = action.payload;

            state.phoneCallData = {
                ...state.phoneCallData,
                initialContactId,
                isIncomingCall,
            };
        },
        setIsDoNotDisturbedEnabled: (state, action: PayloadAction<boolean>) => {
            if (state.features['DisplayMissedCallsLog']) {
                state.phoneCallData = {
                    ...state.phoneCallData,
                    isDoNotDisturbedEnabled: action.payload,
                };
                localStorage.setItem('isDndEnabled', JSON.stringify(action.payload));
            } else {
                state.phoneCallData = {
                    ...state.phoneCallData,
                    isDoNotDisturbedEnabled: false,
                };
            }
        },
        setQueueARN: (
            state,
            action: PayloadAction<
                {
                    endpointARN?: string;
                    endpointId?: string;
                    type: string;
                    name: string;
                    phoneNumber: string;
                    agentLogin: string;
                    queue?: string;
                }[]
            >,
        ) => {
            state.phoneCallData = {...state.phoneCallData, queueARN: action.payload};
        },
        setConnectingPhoneInboundStatuses: (
            state,
            action: PayloadAction<{
                isOutboundCallActive: boolean;
                showAnswerButton: boolean;
                initialContactId: string;
                sourceAgentEmail: string;
                isIncomingCall: boolean;
                callerPatientId?: string | number;
                callerFirstName?: string;
                callerLastName?: string;
                callerSex?: string;
                callerDOB?: string;
            }>,
        ) => {
            const {
                isOutboundCallActive,
                showAnswerButton,
                initialContactId,
                sourceAgentEmail,
                isIncomingCall,
                callerPatientId,
                callerFirstName,
                callerLastName,
                callerSex,
                callerDOB,
            } = action.payload;

            state.phoneCallData = {
                ...state.phoneCallData,
                isOutboundCallActive,
                showAnswerButton,
                initialContactId,
                sourceAgentEmail,
                isIncomingCall,
                callerPatientId,
                callerFirstName,
                callerLastName,
                callerSex,
                callerDOB,
            };
        },
        acceptContactSuccess: (state, action: PayloadAction<boolean>) => {
            state.phoneCallData = {
                ...state.phoneCallData,
                showAnswerButton: action.payload,
            };
        },
        disconnectContactSuccess: (
            state,
            action: PayloadAction<{needToShowCPPPanel: boolean; callerPatientId: string | number}>,
        ) => {
            const {needToShowCPPPanel, callerPatientId} = action.payload;

            state.phoneCallData = {
                ...state.phoneCallData,
                needToShowCPPPanel,
                callerPatientId,
            };
        },
        setTransferCallStatus: (state, action: PayloadAction<boolean>) => {
            state.phoneCallData = {
                ...state.phoneCallData,
                needToShowCPPPanel: action.payload,
            };
        },
        refreshUserFeatures: (state) => state,
        refreshUserFeaturesSuccess: (state, action: PayloadAction<{[key: string]: boolean}>) => {
            state.features = action.payload;
        },
        setIsWSConnected: (state, action: PayloadAction<boolean>) => {
            state.isWSConnected = action.payload;
        },
    },
});

export const {
    setUserInitialData,
    getUserInitialData,
    setSelectedMissionControl,
    setOutboundCallStatus,
    setAwsConnectAgentStatus,
    setIsDoNotDisturbedEnabled,
    setConnectingPhoneStatuses,
    setQueueARN,
    setConnectingPhoneInboundStatuses,
    acceptContactSuccess,
    disconnectContactSuccess,
    setTransferCallStatus,
    updateUserData,
    updateUserDataSuccess,
    refreshUserFeatures,
    refreshUserFeaturesSuccess,
    toggleLeftSidebar,
    setIsWSConnected,
} = actions;

export default reducer;
